import React, { useCallback, useEffect, useState } from 'react';
import { X, Video } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { fetchJsonCached } from '../../lib/settingsCache';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Modal player - streams are requested to open in a modal (not a slide-out). */
const StreamModal = ({ stream, locationName, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!stream) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${locationName} live stream`}
      data-testid="stream-modal"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="stream-modal-overlay" />

      <div className="relative z-10 w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-slate-200 bg-slate-50">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-blue-700">{locationName}</p>
            <h2 className="text-lg font-bold text-slate-900 truncate">{stream.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition-colors shrink-0"
            aria-label="Close stream"
            data-testid="stream-modal-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="bg-black aspect-video">
          <iframe
            key={stream.embed_url}
            src={stream.embed_url}
            title={`${locationName} - ${stream.name}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            data-testid="stream-modal-iframe"
          />
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200">
          <a
            href={stream.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-700 hover:underline font-medium"
          >
            Watch on YouTube
          </a>
        </div>
      </div>
    </div>
  );
};

const WebcamsPage = () => {
  const { webcams_enabled, _loaded } = useSiteFeatureFlags();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setSeoMetadata({
      title: 'St John Live Webcams | Live Island Views | St John USVI',
      description:
        'Watch live webcams from around St John, US Virgin Islands. Click a location to open the live feed and see the beaches, bars and bays in real time.',
      keywords: 'st john live webcams, st john usvi webcam, live island cams, cruz bay webcam, coral bay webcam',
      canonicalPath: '/island-info/st-john-live-webcams',
      ogType: 'website',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchJsonCached(`${API_URL}/api/public/webcams/locations`);
        if (!cancelled) setLocations(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) setLocations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const openStream = useCallback((stream, name) => {
    setActive({ stream, locationName: name });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          <header className="text-center mb-4">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold italic uppercase text-slate-900 tracking-tight">
              St John Live Webcams
            </h1>
            <p className="font-script text-3xl text-cyan-500 mt-3">St John, Virgin Islands</p>
          </header>

          <div className="max-w-3xl mx-auto text-center mb-12 space-y-1">
            <p className="font-semibold text-slate-700">
              Here is a collection of free live cams throughout the island of St John.
            </p>
            <p className="font-semibold text-slate-700">
              Simply click the &ldquo;view cam&rdquo; button and the live feed will open, then click to play.
            </p>
          </div>

          {loading || !_loaded ? (
            <div className="flex justify-center py-16" data-testid="webcams-loading">
              <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !webcams_enabled ? (
            <p className="text-center text-slate-500 py-16" data-testid="webcams-disabled">
              Live webcams are temporarily unavailable. Please check back soon.
            </p>
          ) : locations.length === 0 ? (
            <p className="text-center text-slate-500 py-16" data-testid="webcams-empty">
              No live cams have been added yet. Check back soon!
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="webcams-grid">
              {locations.map((location) => {
                const cover =
                  resolveMediaUrl(location.cover_image_url) ||
                  location.streams[0]?.thumbnail_url ||
                  '';
                return (
                  <article
                    key={location.id}
                    className="rounded-2xl border border-slate-200 bg-white shadow-md hover:shadow-xl transition-shadow overflow-hidden flex flex-col"
                    data-testid={`webcam-location-${location.slug}`}
                  >
                    <div className="relative aspect-video bg-slate-100">
                      {cover ? (
                        <img
                          src={cover}
                          alt={location.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                          <Video className="w-10 h-10" />
                        </div>
                      )}

                      {location.logo_url && (
                        <img
                          src={resolveMediaUrl(location.logo_url)}
                          alt={`${location.name} logo`}
                          loading="lazy"
                          className="absolute top-2 left-2 h-10 w-auto max-w-[38%] object-contain bg-white/90 rounded-lg p-1 shadow-md z-10"
                          data-testid={`webcam-logo-${location.slug}`}
                        />
                      )}

                      {/* Stream buttons sit ON the cover image */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3">
                        {location.streams.map((stream, index) => (
                          <button
                            key={`${location.id}-${index}`}
                            onClick={() => openStream(stream, location.name)}
                            className="inline-flex items-center gap-2 pl-3 pr-4 py-1.5 bg-red-600 text-white font-bold text-xs sm:text-sm rounded-full shadow-[0_2px_10px_rgba(0,0,0,0.45)] hover:bg-red-700 hover:scale-105 transition-all"
                            data-testid={`webcam-stream-btn-${location.slug}-${index + 1}`}
                          >
                            <Video className="w-4 h-4 shrink-0" />
                            <span className="whitespace-nowrap">{stream.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="px-5 py-4 text-center">
                      <h2 className="text-base sm:text-lg font-bold uppercase tracking-wide text-slate-900">
                        {location.name}
                      </h2>
                      {location.description && (
                        <p className="text-sm text-slate-600 mt-1">{location.description}</p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {active && (
        <StreamModal
          stream={active.stream}
          locationName={active.locationName}
          onClose={() => setActive(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default WebcamsPage;
